import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip, Grid,
  Menu, MenuItem, ListItemIcon, ListItemText, FormControl, InputLabel, Select, Button, Skeleton,
  Card, CardContent, Avatar, IconButton, Badge, Dialog,
  DialogTitle, DialogContent, DialogActions, Tooltip, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, Alert, OutlinedInput, TextField, Checkbox
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import MovieCreationIcon from '@mui/icons-material/MovieCreation';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import CheckIcon from '@mui/icons-material/Check';
import Divider from '@mui/material/Divider';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  DndContext as TestDndContext,
  PointerSensor as TestPointerSensor,
  TouchSensor as TestTouchSensor,
  useSensor as useTestSensor,
  useSensors as useTestSensors,
  useDraggable as useTestDraggable,
  useDroppable as useTestDroppable,
} from '@dnd-kit/core';
import api from '../../api';
import QuizIcon from '@mui/icons-material/Quiz';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import SchoolIcon from '@mui/icons-material/School';
import TopicIcon from '@mui/icons-material/Topic';
import TimerIcon from '@mui/icons-material/Timer';
import VideoExplanationPlayer from '../../components/VideoExplanationPlayer';
import { PreviewLatex } from '../../components/PreviewLatex';
import { adminHasPermission } from '../../utils/adminPermissions';

const normSchoolYear = (s) => String(s || '').trim().toLowerCase();

const isYear4Course = (c) => normSchoolYear(c?.school_year) === 'year 4';

const displayCourseName = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  return raw.replace(/\s*[-–]\s*\d{4}\/\d{4}\s*$/, '').trim() || raw;
};

// Categorise a question_type string the same way WorkSheetTake does
const getAdminQuestionCategory = (qt = '') => {
  const t = (qt || '').toLowerCase();
  if (t.includes('writing') || t.includes('composition')) return 'writing';
  if (t.includes('reading') || t.includes('comprehension') || t.includes('passage')) return 'reading_comprehension';
  if ((t.includes('fill') && t.includes('blank')) || t === 'fill-in-the-blank') return 'fill_blank';
  if (t.includes('short answer') || t === 'short-answer' || t === 'short_answer') return 'short_answer';
  if (t.includes('vocab') || t.includes('match')) return 'vocab_matching';
  return 'mcq';
};

const IN_PROGRESS_VIDEO_STATUSES = ['pending', 'parsing', 'rendering', 'composing', 'uploading'];

const LIST_CHIP_MULTILINE_SX = {
  maxWidth: '100%',
  height: 'auto',
  alignSelf: 'flex-start',
  '& .MuiChip-label': {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    lineHeight: 1.35,
    display: 'block',
    py: 0.35,
  },
};

const DIFFICULTY_LEVELS = [
  { value: 'Easy', chilies: 1, color: '#1f9d55', bg: '#e6f7ec' },
  { value: 'Medium', chilies: 2, color: '#c9821a', bg: '#fff4e0' },
  { value: 'Hard', chilies: 3, color: '#d64545', bg: '#fde8e8' },
];

function DraggableTableRow({ testId, enabled, children, sx, MuiTableRow }) {
  const { attributes, listeners, setNodeRef, isDragging } = useTestDraggable({ id: `test-${testId}`, disabled: !enabled });
  const Comp = MuiTableRow;
  return (
    <Comp
      ref={enabled ? setNodeRef : undefined}
      {...(enabled ? attributes : {})}
      {...(enabled ? listeners : {})}
      sx={{ ...sx, ...(enabled ? { cursor: 'grab', opacity: isDragging ? 0.4 : 1 } : {}) }}
    >
      {children}
    </Comp>
  );
}

function ReassignWeekDropzone({ week, plan }) {
  const { setNodeRef, isOver } = useTestDroppable({ id: `reassign-week-${week.id}` });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        p: 1,
        mb: 0.5,
        border: '1px dashed',
        borderColor: isOver ? '#4338ca' : '#cbd5e1',
        borderRadius: 1,
        bgcolor: isOver ? '#eef2ff' : 'transparent',
      }}
    >
      <Typography variant="caption" fontWeight={600}>
        Week {week.week_number}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {week.start_date} – {week.end_date} · {week.allocated_test_count}t
      </Typography>
    </Box>
  );
}

const TestList = () => {
  const navigate = useNavigate();
  const canViewAssignments = adminHasPermission('view-assignments');
  const canCreateAssignments = adminHasPermission('create-assignments');
  const canUpdateAssignments = adminHasPermission('update-assignments');
  const canDeleteAssignments = adminHasPermission('delete-assignments');
  const canUseAiAssignmentCreator = adminHasPermission('use-ai-assignment-creator');
  const canAccessAssignmentTests =
    canViewAssignments ||
    canCreateAssignments ||
    canUpdateAssignments ||
    canDeleteAssignments ||
    canUseAiAssignmentCreator;

  const [nameFilter, setNameFilter] = useState('');

  const [filters, setFilters] = useState({
    school_year_id: '',
    academic_course_id: '',
    subject_id: '',
    assignment_id: '',
    course_plan_id: '',
    plan_week_id: '',
    topic_ids: [],
    subtopic_ids: [],
    difficulties: [],
  });

  const [dropdownData, setDropdownData] = useState({
    schoolYears: [],
    academicCourses: [],
    subjects: [],
    assignments: [],
    topics: [],
    topicTree: [],
    subtopics: [],
    plansWithWeeks: [],
  });
  const [reassignMode, setReassignMode] = useState(false);
  const testDndSensors = useTestSensors(useTestSensor(TestPointerSensor), useTestSensor(TestTouchSensor));

  const handleReassignDragEnd = (event) => {
    const overId = event.over?.id;
    const activeId = event.active?.id;
    if (!overId || !activeId) return;
    const testIdMatch = String(activeId).match(/^test-(\d+)$/);
    const weekIdMatch = String(overId).match(/^reassign-week-(\d+)$/);
    if (!testIdMatch || !weekIdMatch) return;
    const testId = Number(testIdMatch[1]);
    const planWeekId = Number(weekIdMatch[1]);
    api.post(`admin/tests/${testId}/reassign`, { plan_week_id: planWeekId })
      .then(() => {
        toast.success('Reassigned');
        fetchTests(false);
      })
      .catch(() => toast.error('Reassign failed'));
  };

  const filteredPlanForReassign = dropdownData.plansWithWeeks.find(
    (p) => String(p.id) === String(filters.course_plan_id)
  );

  const allAcademicCourses = dropdownData.academicCourses || [];
  const filteredAcademicCourses = useMemo(() => (
    filters.school_year_id
      ? allAcademicCourses.filter((c) => String(c.school_year_id) === String(filters.school_year_id))
      : allAcademicCourses
  ), [allAcademicCourses, filters.school_year_id]);

  const [tests, setTests] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, test: null });
  const [assignmentViewDialog, setAssignmentViewDialog] = useState({ open: false, test: null });
  const [deletingTestId, setDeletingTestId] = useState(null);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [imageErrors, setImageErrors] = useState(new Set());
  const [imageLoading, setImageLoading] = useState(new Set());
  const [actionQuestionId, setActionQuestionId] = useState(null);
  const [modalDownloadMenuAnchor, setModalDownloadMenuAnchor] = useState(null);
  const [modalVideoMenuAnchor, setModalVideoMenuAnchor] = useState(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState({});
  const [generatingVideosTestId, setGeneratingVideosTestId] = useState(null);
  const [removingVideosTestId, setRemovingVideosTestId] = useState(null);
  const [togglingStatusTestId, setTogglingStatusTestId] = useState(null);
  const [statusDialog, setStatusDialog] = useState({ open: false, test: null, action: null });
  const [passageEditState, setPassageEditState] = useState(null);
  const [renamingTestId, setRenamingTestId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);

  const refetchSelectedTest = async () => {
    const tid = selectedTestId;
    if (!tid) return;
    try {
      const res = await api.get('admin/assign/test', { params: { ...filters, test_id: tid } });
      const list = res.data?.data?.data ?? [];
      const found = list.find((t) => String(t.test_id || t.id || t.ID) === String(tid));
      if (found) setSelectedTest(found);
    } catch (e) {
      toast.error('Failed to refresh test');
    }
  };

  const openPassageEdit = (q) => {
    setPassageEditState({ questionId: q.id, text: q.passage_text || '', saving: false });
  };

  const savePassageEdit = async () => {
    if (!passageEditState?.questionId) return;
    setPassageEditState((prev) => ({ ...prev, saving: true }));
    try {
      await api.put(`admin/ai/question/${passageEditState.questionId}/passage`, {
        passage_text: passageEditState.text,
      });
      const updatedText = passageEditState.text;
      setSelectedTest((prev) => {
        if (!prev?.questions) return prev;
        return {
          ...prev,
          questions: prev.questions.map((item) =>
            item.id === passageEditState.questionId ? { ...item, passage_text: updatedText } : item
          ),
        };
      });
      setTests((prev) =>
        prev.map((test) => {
          if (!test.questions) return test;
          const tid = test.test_id || test.id || test.ID;
          if (String(tid) !== String(selectedTestId)) return test;
          return {
            ...test,
            questions: test.questions.map((item) =>
              item.id === passageEditState.questionId ? { ...item, passage_text: updatedText } : item
            ),
          };
        })
      );
      toast.success('Passage updated');
      setPassageEditState(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update passage');
      setPassageEditState((prev) => (prev ? { ...prev, saving: false } : prev));
    }
  };

  const buildDownloadKey = (testId, variant, questionNumber = null) =>
    `${testId}-${variant}${questionNumber ? `-${questionNumber}` : ''}`;
 
  const selectedTestId = selectedTest?.id || selectedTest?.test_id || selectedTest?.ID || null;
  const modalPaperKey = selectedTestId ? buildDownloadKey(selectedTestId, 'paper') : null;
  const modalQuestionPaperKey = selectedTestId ? buildDownloadKey(selectedTestId, 'question-paper') : null;
  const modalAnswerSheetKey = selectedTestId ? buildDownloadKey(selectedTestId, 'answer-sheet') : null;
  const modalAnswersKey = selectedTestId ? buildDownloadKey(selectedTestId, 'answers') : null;
  const isModalDownloadingPaper =
    (modalPaperKey && downloadingKey === modalPaperKey) ||
    (modalQuestionPaperKey && downloadingKey === modalQuestionPaperKey);
  const isModalDownloadingAnswerSheet = modalAnswerSheetKey && downloadingKey === modalAnswerSheetKey;
  const isModalDownloadingAnswers = modalAnswersKey && downloadingKey === modalAnswersKey;
  const isModalDownloadingAny =
    isModalDownloadingPaper || isModalDownloadingAnswerSheet || isModalDownloadingAnswers;

  const makeAbsoluteUrl = (url) => {
    if (!url || typeof url !== 'string') return url;
    const trimmed = url.trim();
    const r2Match = trimmed.match(/^https?:\/\/pub-[^/]+\.r2\.dev\/(.+)$/);
    if (r2Match) {
      const baseURL = api.defaults.baseURL || '';
      return `${baseURL.replace(/\/$/, '')}/proxy/visual/${r2Match[1]}`;
    }
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    if (trimmed.startsWith('//')) {
      return `https:${trimmed}`;
    }
    const baseURL = api.defaults.baseURL || '';
    if (baseURL && !trimmed.startsWith('/')) {
      return `${baseURL.replace(/\/$/, '')}/${trimmed}`;
    }
    if (baseURL && trimmed.startsWith('/')) {
      return `${baseURL.replace(/\/$/, '')}${trimmed}`;
    }
    return trimmed;
  };
 
  const resolveVisualAsset = (visualImageUrl, visual) => {
    const urlCandidate = [
      visualImageUrl,
      visual?.url,
      visual?.r2_url,
      visual?.public_url,
      visual?.image_url,
      visual?.src,
    ].find((value) => typeof value === 'string' && value.trim().length > 0);

    if (urlCandidate) {
      return { type: 'image', src: makeAbsoluteUrl(urlCandidate), alt: visual?.alt };
    }

    if (visual?.svg) {
      return { type: 'svg', svg: visual.svg, alt: visual?.alt };
    }

    return null;
  };

  const getQuestionText = (question) => (
    question?.question_text
    || question?.name
    || question?.question
    || question?.text
    || ''
  );

  const getQuestionExplanation = (question) => (
    question?.explanation
    || question?.explanation_text
    || question?.explanationHtml
    || ''
  );

  const hasRenderableRichText = (html) => {
    const value = String(html || '');
    if (!value) return false;
    if (/<img\b[^>]*>/i.test(value)) return true;
    const text = value
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, '');
    return text.length > 0;
  };

  const getOptionText = (option) => (
    option?.option_text
    || option?.name
    || option?.text
    || ''
  );

  const collectImageUrls = (...candidates) => {
    const out = [];
    candidates.forEach((candidate) => {
      if (!candidate) return;
      if (Array.isArray(candidate)) {
        candidate.forEach((entry) => {
          if (typeof entry === 'string' && entry.trim()) out.push(makeAbsoluteUrl(entry));
        });
        return;
      }
      if (typeof candidate === 'string' && candidate.trim()) {
        out.push(makeAbsoluteUrl(candidate));
      }
    });
    return Array.from(new Set(out));
  };

  /** While the details modal is open and any question has in-progress video generation, refresh test data on an interval */
  useEffect(() => {
    if (!modalOpen) return;
    const tid = selectedTest?.id || selectedTest?.test_id || selectedTest?.ID;
    if (!tid || !Array.isArray(selectedTest?.questions)) return;

    const hasInProgress = selectedTest.questions.some(
      (q) => q && IN_PROGRESS_VIDEO_STATUSES.includes(q.video_generation_status)
    );
    if (!hasInProgress) return;

    const poll = async () => {
      try {
        const res = await api.get('admin/assign/test', { params: { ...filters, test_id: tid } });
        const list = res.data?.data?.data ?? [];
        const found = list.find((t) => String(t.test_id || t.id || t.ID) === String(tid));
        if (found) setSelectedTest(found);
      } catch {
        // ignore during background refresh
      }
    };

    void poll();
    const pollTimer = setInterval(poll, 5000);
    return () => clearInterval(pollTimer);
  }, [modalOpen, selectedTest, filters]);

  useEffect(() => {
    fetchTests(false);
    api.get('admin/assign/school-years')
      .then((res) => setDropdownData((prev) => ({ ...prev, schoolYears: res.data.data || [] })))
      .catch(() => toast.error('Failed to fetch school years'));
    api.get('admin/ca_records', { params: { active_only: 1 } })
      .then((res) => {
        const all = res.data.data || [];
        setDropdownData((prev) => ({ ...prev, academicCourses: all }));
        setFilters((prev) => {
          if (!prev.academic_course_id) return prev;
          const stillValid = all.some((c) => String(c.id) === String(prev.academic_course_id));
          return stillValid ? prev : { ...prev, academic_course_id: '', assignment_id: '', course_plan_id: '', plan_week_id: '' };
        });
      })
      .catch(() => toast.error('Failed to fetch academic courses'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Subjects are scoped by school year (shared across every course/batch in that year),
  // independent of picking a specific Academic Course.
  useEffect(() => {
    const { school_year_id } = filters;
    if (!school_year_id) {
      setDropdownData(prev => ({ ...prev, subjects: [] }));
      return;
    }

    api.get(`admin/assign/school-year-subjects/${school_year_id}`)
      .then(res => {
        setDropdownData(prev => ({ ...prev, subjects: res.data.data || [] }));
      })
      .catch(() => toast.error('Failed to fetch subjects'));
  }, [filters.school_year_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Duration (assignment) options + plan/weeks are still tied to one specific Academic Course batch.
  useEffect(() => {
    const { academic_course_id } = filters;
    if (!academic_course_id) {
      setDropdownData(prev => ({ ...prev, assignments: [], plansWithWeeks: [] }));
      return;
    }

    api.get(`admin/ca_based_weeks_subjects/${academic_course_id}`)
      .then(res => {
        const { assignments = [] } = res.data.data || {};
        setDropdownData(prev => ({ ...prev, assignments }));
        setFilters(prev => ({ ...prev, assignment_id: '' }));
      })
      .catch(() => toast.error('Failed to fetch assignments'));

    api.get(`admin/acdemic-courses/${academic_course_id}/course-plans-with-weeks`)
      .then(r => setDropdownData(prev => ({ ...prev, plansWithWeeks: r.data?.plans || [] })))
      .catch(() => setDropdownData(prev => ({ ...prev, plansWithWeeks: [] })));
  }, [filters.academic_course_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const { school_year_id, subject_id } = filters;
    if (!school_year_id || !subject_id) {
      setDropdownData(prev => ({ ...prev, topics: [], topicTree: [], subtopics: [] }));
      return;
    }

    api.get(`admin/fetch/topics/by-school-year-subject/${school_year_id}/${subject_id}`)
      .then(res => {
        const tree = (res.data.data || []).map((t) => ({
          id: t.id,
          name: t.name,
          sort_order: t.sort_order ?? 0,
          subtopics: Array.isArray(t.subtopics) ? t.subtopics : (Array.isArray(t.children) ? t.children : []),
        }));
        setDropdownData(prev => ({
          ...prev,
          topics: tree.map(({ id, name }) => ({ id, name })),
          topicTree: tree,
          subtopics: [],
        }));

        setFilters(prev => ({
          ...prev,
          topic_ids: [],
          subtopic_ids: [],
        }));
      })
      .catch(() => toast.error('Failed to fetch topics'));
  }, [filters.subject_id, filters.school_year_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableSubtopics = useMemo(() => {
    const tree = dropdownData.topicTree || [];
    const selectedTopicIds = Array.isArray(filters.topic_ids) ? filters.topic_ids.map(String) : [];
    const seen = new Set();
    const merged = [];

    tree.forEach((topic) => {
      if (selectedTopicIds.length > 0 && !selectedTopicIds.includes(String(topic.id))) return;
      (topic.subtopics || []).forEach((sub) => {
        const key = String(sub.id);
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(sub);
      });
    });

    return merged;
  }, [dropdownData.topicTree, filters.topic_ids]);

  useEffect(() => {
    setDropdownData(prev => ({ ...prev, subtopics: availableSubtopics }));
    setFilters(prev => {
      const allowed = new Set(availableSubtopics.map((s) => String(s.id)));
      const next = (prev.subtopic_ids || []).filter((sid) => allowed.has(String(sid)));
      if (next.length === (prev.subtopic_ids || []).length && next.every((id, i) => String(id) === String(prev.subtopic_ids[i]))) {
        return prev;
      }
      return { ...prev, subtopic_ids: next };
    });
  }, [availableSubtopics]);
  const fetchTests = async (applyFilters = true, overrides = {}) => {
    setLoading(true);
    try {
      const currentPage = overrides.page ?? page;
      const currentRowsPerPage = overrides.rowsPerPage ?? rowsPerPage;
      const params = applyFilters
        ? {
            ...filters,
            topic_ids: filters.topic_ids?.length ? filters.topic_ids : undefined,
            subtopic_ids: filters.subtopic_ids?.length ? filters.subtopic_ids : undefined,
            difficulties: filters.difficulties?.length ? filters.difficulties : undefined,
            name: nameFilter || undefined,
            page: currentPage + 1,
            per_page: currentRowsPerPage,
          }
        : { page: 1, per_page: currentRowsPerPage };
      const res = await api.get('admin/assign/test', { params });
      const paginator = res.data.data;
      const list = Array.isArray(paginator?.data) ? paginator.data : [];
      setTests(list);
      setTotal(paginator?.total ?? 0);
    } catch (err) {
      toast.error('Failed to fetch test data');
      setTests([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentsChanged = async () => {
    await fetchTests(true);
    if (modalOpen && selectedTestId) {
      await refetchSelectedTest();
    }
  };

  const handleDownloadPdf = async (test, variant = 'question-paper', options = {}) => {
    if (!canAccessAssignmentTests) {
      toast.error('You do not have permission to download worksheet PDFs.');
      return;
    }
    const testId = test?.id || test?.test_id || test?.ID;
    if (!testId) {
      toast.error('Cannot download: Test ID not found');
      return;
    }

    const key = buildDownloadKey(testId, variant, options.questionNumber);
    setDownloadingKey(key);

    try {
      const params = new URLSearchParams();
      params.set('variant', variant);
      if (options.questionNumber) {
        params.set('question_number', options.questionNumber);
      }
      if (options.questionId) {
        params.set('question_id', options.questionId);
      }

      const response = await api.get(`admin/assign/test/${testId}/download-pdf?${params.toString()}`, {
        responseType: 'blob',
      });

      const contentTypeHeader = (response.headers && response.headers['content-type']) || '';
      const isPdf = contentTypeHeader.toLowerCase().includes('application/pdf');
      const bodySize = response.data && typeof response.data.size === 'number' ? response.data.size : (response.data && response.data.byteLength) || 0;
      const minPdfSize = 200;
      const knownSmall = bodySize > 0 && bodySize < minPdfSize;
      if (!isPdf || knownSmall) {
        let errorMessage = !isPdf ? 'Server did not return a PDF.' : 'PDF response too small (likely an error).';
        try {
          const text = await (response.data instanceof Blob ? response.data.text() : Promise.resolve(String(response.data)));
          const parsed = JSON.parse(text);
          if (parsed && typeof parsed.message === 'string') errorMessage = parsed.message;
        } catch (_) {}
        toast.error(errorMessage);
        setDownloadingKey(null);
        return;
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeName = (test?.test_name || `test-${testId}`)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const suffix = options.questionNumber
        ? `question-${options.questionNumber}`
        : variant.replace(/_/g, '-');
      link.href = url;
      link.download = `${safeName || `test-${testId}`}-${suffix}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF download started');
    } catch (error) {
      console.error('Error downloading test PDF', error);
      const message = error?.response?.data?.message || 'Failed to download PDF';
      toast.error(message);
    } finally {
      setDownloadingKey(null);
    }
  };

  const handleVideoGenerate = async (test) => {
    if (!canUpdateAssignments) {
      toast.error('You do not have permission to update worksheets.');
      return;
    }
    const testId = test?.id || test?.test_id || test?.ID;
    if (!testId) {
      toast.error('Cannot generate videos: Test ID not found');
      return;
    }
    setGeneratingVideosTestId(testId);
    try {
      const res = await api.post(`admin/assign/test/${testId}/generate-videos`);
      const count = res.data?.data?.jobs_dispatched ?? 0;
      toast.success(count > 0 ? `${count} video(s) queued for generation` : 'No questions need videos');
      if (selectedTestId === testId) await refetchSelectedTest();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to queue video generation');
    } finally {
      setGeneratingVideosTestId(null);
    }
  };

  const handleRemoveAllVideos = async (test) => {
    if (!canUpdateAssignments) {
      toast.error('You do not have permission to update worksheets.');
      return;
    }
    const testId = test?.id || test?.test_id || test?.ID;
    if (!testId) {
      toast.error('Cannot remove videos: Test ID not found');
      return;
    }
    
    if (!window.confirm('Are you sure you want to remove ALL explanation videos from this test? This action cannot be undone.')) {
      return;
    }
    
    setRemovingVideosTestId(testId);
    try {
      const res = await api.delete(`admin/assign/test/${testId}/clear-all-videos`);
      const count = res.data?.data?.videos_cleared ?? 0;
      toast.success(count > 0 ? `${count} video(s) removed successfully` : 'No videos to remove');
      if (selectedTestId === testId) await refetchSelectedTest();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to remove videos');
    } finally {
      setRemovingVideosTestId(null);
    }
  };

  const handleEdit = (test) => {
    if (!canUpdateAssignments) {
      toast.error('You do not have permission to edit worksheets.');
      return;
    }
    const testId = test.id || test.test_id || test.ID;
    if (!testId) {
      console.error('Test object missing ID field:', test);
      toast.error('Cannot edit: Test ID not found');
      return;
    }
    console.log('🔵 Edit test clicked, ID:', testId);
    navigate(`/admin/course-test/${testId}`);
  };

  const handleEditQuestion = (questionIndex) => {
    if (!canUpdateAssignments) {
      toast.error('You do not have permission to edit worksheets.');
      return;
    }
    const testId = selectedTestId;
    if (!testId) {
      toast.error('Cannot edit: Test ID not found');
      return;
    }
    navigate(`/admin/course-test/${testId}`, { state: { focusQuestionIndex: questionIndex } });
  };

  const handleDeleteClick = (test) => {
    if (!canDeleteAssignments) {
      toast.error('You do not have permission to delete worksheets.');
      return;
    }
    setDeleteDialog({ open: true, test });
  };

  const handleDeleteConfirm = async () => {
    const test = deleteDialog.test;
    if (!test || !canDeleteAssignments) return;

    const testId = test.id || test.test_id || test.ID;
    if (!testId) {
      toast.error('Cannot delete: Test ID not found');
      setDeleteDialog({ open: false, test: null });
      return;
    }

    setDeletingTestId(testId);
    console.log('🗑️ Attempting to delete test ID:', testId);
    
    try {
      const response = await api.delete(`admin/assign/test/${testId}`);
      console.log('✅ Delete test response:', response.data);
      
      if (response.data.success) {
        toast.success(response.data.message || 'Test deleted successfully');
        setDeleteDialog({ open: false, test: null });
        fetchTests(true);
      }
    } catch (err) {
      console.error('❌ Error deleting test:', err);
      if (err.response?.status === 422) {
        toast.error(err.response.data.error || 'Cannot delete test');
      } else if (err.response?.status === 404) {
        toast.error('Test not found');
        fetchTests(true);
      } else if (err.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error(err.response?.data?.error || 'Failed to delete test');
      }
    } finally {
      setDeletingTestId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, test: null });
  };

  const isTestActive = (test) => {
    return test?.status === 2 || test?.status === null || test?.status === undefined;
  };

  const handleToggleStatusClick = (test) => {
    if (!canUpdateAssignments) {
      toast.error('You do not have permission to update worksheets.');
      return;
    }
    const action = isTestActive(test) ? 'deactivate' : 'activate';
    setStatusDialog({ open: true, test, action });
  };

  const handleToggleStatusConfirm = async () => {
    const { test, action } = statusDialog;
    if (!test || !canUpdateAssignments) return;

    const testId = test.id || test.test_id || test.ID;
    if (!testId) {
      toast.error('Cannot update status: Test ID not found');
      setStatusDialog({ open: false, test: null, action: null });
      return;
    }

    setTogglingStatusTestId(testId);
    try {
      const response = await api.patch(`admin/assign/test/${testId}/toggle-status`, { action });
      if (response.data.success) {
        toast.success(response.data.message || `Test ${action}d successfully`);
        setStatusDialog({ open: false, test: null, action: null });
        fetchTests(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || `Failed to ${action} test`);
    } finally {
      setTogglingStatusTestId(null);
    }
  };

  const handleToggleStatusCancel = () => {
    setStatusDialog({ open: false, test: null, action: null });
  };

  const startRename = (test) => {
    const testId = test.id || test.test_id || test.ID;
    setRenamingTestId(testId);
    setRenameValue(test.test_name || '');
  };

  const cancelRename = () => {
    setRenamingTestId(null);
    setRenameValue('');
  };

  const saveRename = async (testId) => {
    if (!renameValue.trim()) return;
    setRenameSaving(true);
    try {
      await api.patch(`admin/assign/test/${testId}/rename`, { name: renameValue.trim() });
      setTests((prev) =>
        prev.map((t) => {
          const tid = t.id || t.test_id || t.ID;
          return String(tid) === String(testId) ? { ...t, test_name: renameValue.trim() } : t;
        })
      );
      if (String(selectedTestId) === String(testId)) {
        setSelectedTest((prev) => prev ? { ...prev, test_name: renameValue.trim() } : prev);
      }
      toast.success('Test renamed successfully');
      setRenamingTestId(null);
      setRenameValue('');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to rename test');
    } finally {
      setRenameSaving(false);
    }
  };

  return (
    <TestDndContext sensors={testDndSensors} onDragEnd={handleReassignDragEnd}>
    <Box sx={{
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh',
      p: 3
    }}>
      {reassignMode && filteredPlanForReassign && (
        <Box sx={{
          position: 'fixed', right: 16, top: 96, width: 260, maxHeight: '70vh', overflowY: 'auto',
          bgcolor: 'white', boxShadow: 3, borderRadius: 2, p: 1.5, zIndex: 1200,
        }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            Drop on a week of {filteredPlanForReassign.name}
          </Typography>
          {filteredPlanForReassign.weeks.map((w) => (
            <ReassignWeekDropzone key={w.id} week={w} plan={filteredPlanForReassign} />
          ))}
        </Box>
      )}
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            width: 56,
            height: 56
          }}>
            <QuizIcon sx={{ fontSize: 28, color: 'white' }} />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 0.5
            }}>
              Worksheet Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage all course worksheets
            </Typography>
          </Box>
        </Box>
        
        {canCreateAssignments && (
          <Button
            onClick={() => navigate('/admin/course-test')}
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 3,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4)'
              }
            }}
          >
            Create New Worksheet
          </Button>
        )}
      </Box>

      {/* Filters Card */}
      <Card sx={{ 
        mb: 4,
        borderRadius: 4,
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)'
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Avatar sx={{ 
              background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
              width: 48,
              height: 48
            }}>
              <FilterListIcon sx={{ color: 'white' }} />
            </Avatar>
            <Typography variant="h6" fontWeight={600}>
              Filter Worksheets
            </Typography>
            <Box sx={{ ml: 'auto' }}>
              <Button
                size="small"
                variant={reassignMode ? 'contained' : 'outlined'}
                color="secondary"
                onClick={() => setReassignMode((v) => !v)}
                disabled={!filters.course_plan_id}
                sx={{ textTransform: 'none' }}
              >
                {reassignMode ? 'Exit Reassign Mode' : 'Reassign Mode'}
              </Button>
              {!filters.course_plan_id && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  Pick a Plan filter first
                </Typography>
              )}
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel sx={{
                  background: 'white',
                  px: 1,
                  '&.Mui-focused': { color: '#667eea' }
                }}>
                  🏫 School Year
                </InputLabel>
                <Select
                  value={filters.school_year_id}
                  label="🏫 School Year"
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    school_year_id: String(e.target.value),
                    academic_course_id: '',
                    subject_id: '',
                    assignment_id: '',
                    course_plan_id: '',
                    plan_week_id: '',
                    topic_ids: [],
                    subtopic_ids: [],
                  }))}
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(102, 126, 234, 0.3)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea'
                    }
                  }}
                >
                  <MenuItem value="">All School Years</MenuItem>
                  {dropdownData.schoolYears.map((opt) => (
                    <MenuItem key={String(opt.id)} value={String(opt.id)}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel sx={{
                  background: 'white',
                  px: 1,
                  '&.Mui-focused': { color: '#667eea' }
                }}>
                  🎓 Academic Course
                </InputLabel>
                <Select
                  value={filters.academic_course_id}
                  label="🎓 Academic Course"
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    academic_course_id: String(e.target.value),
                    assignment_id: '',
                    course_plan_id: '',
                    plan_week_id: '',
                  }))}
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(102, 126, 234, 0.3)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea'
                    }
                  }}
                >
                  <MenuItem value="">All Courses</MenuItem>
                  {filteredAcademicCourses.map((opt) => (
                    <MenuItem key={String(opt.id)} value={String(opt.id)}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: 480 }}>
                        <PreviewLatex html={displayCourseName(opt.course_name || opt.name)} />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {[
              { key: 'course_plan_id', label: 'Plan / Batch', icon: '🗂️', options: dropdownData.plansWithWeeks, multiple: false },
              { key: 'assignment_id', label: 'Duration', icon: '📝', options: dropdownData.assignments, multiple: false },
            ].map(({ key, label, icon, options, multiple }) => {
              const value = multiple ? (Array.isArray(filters[key]) ? filters[key] : []) : (filters[key] ?? '');
              return (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <FormControl fullWidth>
                  <InputLabel sx={{
                    background: 'white',
                    px: 1,
                    '&.Mui-focused': { color: '#667eea' }
                  }}>
                    {icon} {label}
                  </InputLabel>
                  <Select
                    multiple={multiple}
                    value={value}
                    label={`${icon} ${label}`}
                    input={<OutlinedInput label={`${icon} ${label}`} />}
                    onChange={(e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }))}
                    sx={{
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(102, 126, 234, 0.3)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea'
                      }
                    }}
                  >
                    {!multiple && <MenuItem value="">All {label}s</MenuItem>}
                    {options.map((opt) => (
                      <MenuItem key={opt.id || opt._id} value={String(opt.id || opt._id)}>
                        {opt.name || opt.assignment_name || opt.subject_name || opt.topic_name || opt.subtopic_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              );
            })}

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth disabled={!filters.school_year_id}>
                <InputLabel sx={{
                  background: 'white',
                  px: 1,
                  '&.Mui-focused': { color: '#667eea' }
                }}>
                  📚 Subject
                </InputLabel>
                <Select
                  value={filters.subject_id}
                  label="📚 Subject"
                  input={<OutlinedInput label="📚 Subject" />}
                  onChange={(e) => setFilters((prev) => ({
                    ...prev,
                    subject_id: String(e.target.value),
                    topic_ids: [],
                    subtopic_ids: [],
                  }))}
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(102, 126, 234, 0.3)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea'
                    }
                  }}
                >
                  <MenuItem value="">All Subjects</MenuItem>
                  {dropdownData.subjects.map((opt) => (
                    <MenuItem key={opt.id || opt._id} value={String(opt.id || opt._id)}>
                      {opt.name || opt.subject_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {[
              {
                key: 'topic_ids',
                label: 'Topic',
                icon: '📖',
                options: dropdownData.topics,
                selected: Array.isArray(filters.topic_ids) ? filters.topic_ids : [],
                disabled: !filters.school_year_id || !filters.subject_id,
                emptyHint: !filters.subject_id ? 'Select a subject first' : 'No topics available',
                countLabel: 'Topics',
              },
              {
                key: 'subtopic_ids',
                label: 'Subtopic',
                icon: '📄',
                options: dropdownData.subtopics,
                selected: Array.isArray(filters.subtopic_ids) ? filters.subtopic_ids : [],
                disabled: !filters.school_year_id || !filters.subject_id,
                emptyHint: !filters.subject_id
                  ? 'Select a subject first'
                  : (filters.topic_ids?.length
                    ? 'No subtopics for selected topics'
                    : 'No subtopics available'),
                countLabel: 'Subtopics',
              },
            ].map(({ key, label, icon, options, selected, disabled, emptyHint, countLabel }) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <FormControl fullWidth disabled={disabled}>
                  <InputLabel sx={{
                    background: 'white',
                    px: 1,
                    '&.Mui-focused': { color: '#667eea' }
                  }}>
                    {icon} {label}
                  </InputLabel>
                  <Select
                    multiple
                    value={selected}
                    label={`${icon} ${label}`}
                    input={<OutlinedInput label={`${icon} ${label}`} />}
                    onChange={(e) => {
                      const next = typeof e.target.value === 'string'
                        ? e.target.value.split(',')
                        : e.target.value;
                      setFilters((prev) => ({
                        ...prev,
                        [key]: next,
                        ...(key === 'topic_ids' ? { subtopic_ids: prev.subtopic_ids } : {}),
                      }));
                    }}
                    renderValue={(sel) => {
                      if (!sel.length) return <em>All</em>;
                      if (sel.length === 1) {
                        const opt = options.find((o) => String(o.id) === String(sel[0]));
                        return opt?.name || sel[0];
                      }
                      return `${sel.length} ${countLabel}`;
                    }}
                    sx={{
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(102, 126, 234, 0.3)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea'
                      }
                    }}
                  >
                    {options.length === 0 ? (
                      <MenuItem disabled dense>
                        <em>{emptyHint}</em>
                      </MenuItem>
                    ) : (
                      options.map((opt) => {
                        const id = String(opt.id || opt._id);
                        return (
                          <MenuItem key={id} value={id} dense>
                            <Checkbox checked={selected.map(String).includes(id)} size="small" />
                            <ListItemText primary={opt.name || opt.topic_name || opt.subtopic_name} />
                          </MenuItem>
                        );
                      })
                    )}
                  </Select>
                </FormControl>
              </Grid>
            ))}

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="🔍 Search Worksheets"
                placeholder="Search by name, subject, topic, subtopic, or difficulty…"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '& fieldset': { borderColor: 'rgba(102, 126, 234, 0.3)' },
                    '&:hover fieldset': { borderColor: '#667eea' },
                    '&.Mui-focused fieldset': { borderColor: '#667eea' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#667eea' },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mr: 0.5 }}>
                Difficulty
              </Typography>
              {DIFFICULTY_LEVELS.map((lvl) => {
                const active = (filters.difficulties || []).includes(lvl.value);
                return (
                  <Chip
                    key={lvl.value}
                    label={`${'🌶️'.repeat(lvl.chilies)} ${lvl.value}`}
                    size="small"
                    clickable
                    onClick={() => setFilters((prev) => ({
                      ...prev,
                      difficulties: active
                        ? (prev.difficulties || []).filter((v) => v !== lvl.value)
                        : [...(prev.difficulties || []), lvl.value],
                    }))}
                    sx={{
                      fontWeight: 600,
                      bgcolor: active ? lvl.bg : 'action.hover',
                      color: active ? lvl.color : 'text.secondary',
                      border: '1px solid',
                      borderColor: active ? lvl.color : 'divider',
                      '&:hover': { bgcolor: active ? lvl.bg : 'action.selected' },
                    }}
                  />
                );
              })}
            </Grid>

            <Grid item xs={12} sm={12} md={4} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                onClick={() => { setPage(0); fetchTests(true, { page: 0 }); }}
                variant="contained"
                startIcon={<SearchIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4)'
                  }
                }}
                fullWidth
              >
                Apply Filters
              </Button>

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setNameFilter('');
                  setPage(0);
                  setFilters({
                    school_year_id: '',
                    academic_course_id: '',
                    subject_id: '',
                    assignment_id: '',
                    course_plan_id: '',
                    plan_week_id: '',
                    topic_ids: [],
                    subtopic_ids: [],
                    difficulties: [],
                  });
                  fetchTests(false, { page: 0 });
                }}
                sx={{
                  borderColor: '#ff9800',
                  color: '#ff9800',
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#f57c00',
                    background: 'rgba(255, 152, 0, 0.1)',
                    transform: 'translateY(-2px)'
                  }
                }}
                fullWidth
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tests Section */}
      {loading ? (
        <Grid container spacing={3}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Grid item xs={12} key={i}>
              <Card sx={{ 
                borderRadius: 4,
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Skeleton variant="circular" width={48} height={48} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="60%" height={32} />
                      <Skeleton variant="text" width="40%" height={20} />
                    </Box>
                  </Box>
                  <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 2 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : tests.length === 0 ? (
        <Card sx={{ 
          borderRadius: 4,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)',
          textAlign: 'center',
          py: 8
        }}>
          <CardContent>
            <Avatar sx={{ 
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              width: 80,
              height: 80,
              mx: 'auto',
              mb: 3
            }}>
              <QuizIcon sx={{ fontSize: 40, color: 'white' }} />
            </Avatar>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              No Worksheets Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No worksheets match the selected criteria. Try adjusting your filters or create a new worksheet.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ 
          borderRadius: 4,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)'
        }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
              <Table
                size="small"
                sx={{
                  tableLayout: 'fixed',
                  width: '100%',
                  minWidth: 1000,
                }}
              >
                <colgroup>
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '6%' }} />
                  <col style={{ width: 172, minWidth: 172 }} />
                </colgroup>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', verticalAlign: 'middle', overflow: 'hidden' }}>
                      📝 Worksheet Name
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', verticalAlign: 'middle', overflow: 'hidden' }}>
                      🎓 Course
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', verticalAlign: 'middle', overflow: 'hidden' }}>
                      📚 Subject
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', verticalAlign: 'middle', overflow: 'hidden' }}>
                      📖 Topic
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', verticalAlign: 'middle', overflow: 'hidden' }}>
                      📄 Subtopic
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', verticalAlign: 'middle', overflow: 'hidden' }}>
                      🎯 Difficulty
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', verticalAlign: 'middle', overflow: 'hidden' }}>
                      ⏱ Duration
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', textAlign: 'center', verticalAlign: 'middle', overflow: 'hidden' }}>
                      ❓ Questions
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '14px', textAlign: 'center', verticalAlign: 'middle', overflow: 'hidden' }}>
                      🔘 Status
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '14px',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        whiteSpace: 'nowrap',
                        width: 172,
                        minWidth: 172,
                        boxSizing: 'border-box',
                      }}
                    >
                      🔧 Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tests.map((test, index) => {
                    const testId = test.id || test.test_id || test.ID;
                    const isDeleting = deletingTestId === testId;
                    const isDownloadingCurrentTest = Boolean(
                      testId && downloadingKey && downloadingKey.startsWith(`${testId}-`)
                    );

                    return (
                      <DraggableTableRow
                        key={testId || index}
                        testId={testId}
                        enabled={reassignMode && Boolean(filteredPlanForReassign)}
                        MuiTableRow={TableRow}
                        sx={{
                          '&:hover': {
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
                          },
                          '&:nth-of-type(even)': {
                            background: 'rgba(0,0,0,0.02)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <TableCell sx={{ verticalAlign: 'top', overflow: 'hidden' }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                            <Avatar sx={{
                              background: 'linear-gradient(135deg, #e91e63 0%, #ad1457 100%)',
                              width: 40,
                              height: 40,
                              flexShrink: 0,
                            }}>
                              <QuizIcon sx={{ fontSize: 20, color: 'white' }} />
                            </Avatar>
                            {renamingTestId === testId ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                                <TextField
                                  size="small"
                                  value={renameValue}
                                  onChange={(e) => setRenameValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveRename(testId);
                                    if (e.key === 'Escape') cancelRename();
                                  }}
                                  autoFocus
                                  disabled={renameSaving}
                                  sx={{ flex: 1, minWidth: 0 }}
                                  inputProps={{ style: { fontSize: 13 } }}
                                />
                                <Tooltip title="Save">
                                  <span>
                                    <IconButton size="small" onClick={() => saveRename(testId)} disabled={renameSaving || !renameValue.trim()}>
                                      {renameSaving ? <CircularProgress size={14} /> : <CheckIcon sx={{ fontSize: 16 }} color="success" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Cancel">
                                  <IconButton size="small" onClick={cancelRename} disabled={renameSaving}>
                                    <CloseIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.35, minWidth: 0, flex: 1 }}
                                >
                                  {test.test_name}
                                </Typography>
                                {canUpdateAssignments && (
                                  <Tooltip title="Rename">
                                    <IconButton
                                      size="small"
                                      onClick={() => startRename(test)}
                                      sx={{ opacity: 0.4, '&:hover': { opacity: 1 }, p: 0.3, flexShrink: 0 }}
                                    >
                                      <DriveFileRenameOutlineIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top', overflow: 'hidden' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {Array.isArray(test.assignments) && test.assignments.length > 0 ? (
                              <>
                                <Chip
                                  icon={<SchoolIcon />}
                                  size="small"
                                  label={`${displayCourseName(test.assignments[0].academic_course_label || 'Course')} • ${test.assignments[0].week_label || 'Week'}`}
                                  sx={{
                                    ...LIST_CHIP_MULTILINE_SX,
                                    background: '#f3e5f5',
                                    color: '#6a1b9a',
                                    fontWeight: 500,
                                  }}
                                />
                                {test.assignments[0].plan_name && (
                                  <Chip
                                    size="small"
                                    label={`${test.assignments[0].plan_name} · Week ${test.assignments[0].plan_week_number ?? '—'}`}
                                    sx={{
                                      ...LIST_CHIP_MULTILINE_SX,
                                      background: '#e0e7ff',
                                      color: '#4338ca',
                                      fontWeight: 500,
                                    }}
                                  />
                                )}
                                {test.assignments.length > 1 && (
                                  <Button
                                    size="small"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => setAssignmentViewDialog({ open: true, test })}
                                    sx={{
                                      alignSelf: 'flex-start',
                                      textTransform: 'none',
                                      px: 0.5,
                                      minWidth: 0,
                                      fontWeight: 600,
                                    }}
                                  >
                                    View all ({test.assignments.length})
                                  </Button>
                                )}
                              </>
                            ) : (
                              <Chip
                                icon={<SchoolIcon />}
                                label={displayCourseName(test.course_name)}
                                size="small"
                                sx={{
                                  ...LIST_CHIP_MULTILINE_SX,
                                  background: '#e3f2fd',
                                  color: '#1976d2',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top', overflow: 'hidden' }}>
                          <Chip 
                            label={test.subject_name || '—'} 
                            size="small" 
                            sx={{ 
                              ...LIST_CHIP_MULTILINE_SX,
                              background: '#e8f5e8', 
                              color: '#2e7d32',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top', overflow: 'hidden' }}>
                          <Chip 
                            icon={<TopicIcon />}
                            label={test.topic_name || '—'} 
                            size="small" 
                            sx={{ 
                              ...LIST_CHIP_MULTILINE_SX,
                              background: '#fff3e0', 
                              color: '#f57c00',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top', overflow: 'hidden' }}>
                          {test.subtopic_name ? (
                            <Tooltip
                              title={test.subtopic_name}
                              placement="top-start"
                              disableHoverListener={test.subtopic_name.length < 48}
                            >
                              <Chip 
                                label={test.subtopic_name} 
                                size="small" 
                                sx={{ 
                                  ...LIST_CHIP_MULTILINE_SX,
                                  background: '#fce4ec', 
                                  color: '#c2185b',
                                  fontWeight: 600,
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                              No subtopic
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top', overflow: 'hidden' }}>
                          {test.difficulty ? (
                            <Chip
                              label={test.difficulty}
                              size="small"
                              sx={{
                                ...LIST_CHIP_MULTILINE_SX,
                                background: test.difficulty.toLowerCase() === 'easy'
                                  ? '#e8f5e9' : test.difficulty.toLowerCase() === 'hard'
                                  ? '#fce4ec' : '#fff3e0',
                                color: test.difficulty.toLowerCase() === 'easy'
                                  ? '#2e7d32' : test.difficulty.toLowerCase() === 'hard'
                                  ? '#c2185b' : '#f57c00',
                                fontWeight: 600,
                                textTransform: 'capitalize',
                              }}
                            />
                          ) : (
                            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>—</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top', overflow: 'hidden' }}>
                          {test.duration_label ? (
                            <Chip
                              icon={<TimerIcon sx={{ fontSize: '16px !important' }} />}
                              label={test.duration_label}
                              size="small"
                              sx={{
                                ...LIST_CHIP_MULTILINE_SX,
                                background: '#ede7f6',
                                color: '#5e35b1',
                                fontWeight: 600,
                              }}
                            />
                          ) : (
                            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <Badge 
                            color="primary"
                            sx={{
                              '& .MuiBadge-badge': {
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                fontSize: '12px',
                                fontWeight: 600
                              }
                            }}
                          >
                            <Avatar sx={{ 
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              width: 32,
                              height: 32,
                              fontSize: '14px',
                              fontWeight: 600
                            }}>
                              {test.questions?.length || 0}
                            </Avatar>
                          </Badge>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <Chip
                            label={isTestActive(test) ? 'Active' : 'Inactive'}
                            size="small"
                            sx={{
                              background: isTestActive(test)
                                ? 'linear-gradient(45deg, #4caf50, #2e7d32)'
                                : 'linear-gradient(45deg, #757575, #616161)',
                              color: 'white',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            whiteSpace: 'nowrap',
                            width: 172,
                            minWidth: 172,
                            maxWidth: 172,
                            overflow: 'visible',
                            boxSizing: 'border-box',
                            px: 0.5,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              flexDirection: 'row',
                              flexWrap: 'nowrap',
                              gap: 0.5,
                              alignItems: 'center',
                              justifyContent: 'center',
                              verticalAlign: 'middle',
                              py: 0.25,
                              '& > *': { flexShrink: 0 },
                            }}
                          >
                            {/* View */}
                            <Tooltip title="View details">
                              <IconButton
                                size="small"
                                onClick={() => { setSelectedTest(test); setModalOpen(true); }}
                                sx={{
                                  border: '1.5px solid #667eea',
                                  color: '#5a4fcf',
                                  width: 32, height: 32,
                                  '&:hover': { backgroundColor: 'rgba(102,126,234,0.12)', transform: 'scale(1.1)' },
                                  transition: 'all 0.2s',
                                }}
                              >
                                <VisibilityIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>

                            {/* Edit */}
                            {canUpdateAssignments && (
                              <Tooltip title="Edit test">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEdit(test)}
                                  sx={{
                                    border: '1.5px solid',
                                    borderColor: 'primary.main',
                                    width: 32, height: 32,
                                    '&:hover': { backgroundColor: 'rgba(25,118,210,0.1)', transform: 'scale(1.1)' },
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {/* Delete */}
                            {canDeleteAssignments && (
                              <Tooltip title="Delete test">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteClick(test)}
                                    disabled={isDeleting}
                                    sx={{
                                      border: '1.5px solid',
                                      borderColor: 'error.main',
                                      width: 32, height: 32,
                                      '&:hover': { backgroundColor: 'rgba(244,67,54,0.1)', transform: 'scale(1.1)' },
                                      transition: 'all 0.2s',
                                    }}
                                  >
                                    {isDeleting ? <CircularProgress size={16} /> : <DeleteOutlineIcon sx={{ fontSize: 17 }} />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {/* More actions (⋮) */}
                            {(canAccessAssignmentTests || canUpdateAssignments) && (
                              <>
                                <Tooltip title="More actions">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => setMoreMenuAnchor((prev) => ({ ...prev, [testId]: e.currentTarget }))}
                                      disabled={isDownloadingCurrentTest || generatingVideosTestId === testId || removingVideosTestId === testId || togglingStatusTestId === testId}
                                      sx={{
                                        border: '1.5px solid rgba(0,0,0,0.23)',
                                        color: 'text.secondary',
                                        width: 32, height: 32,
                                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)', transform: 'scale(1.1)' },
                                        transition: 'all 0.2s',
                                      }}
                                    >
                                      {(isDownloadingCurrentTest || generatingVideosTestId === testId || removingVideosTestId === testId || togglingStatusTestId === testId)
                                        ? <CircularProgress size={16} />
                                        : <MoreVertIcon sx={{ fontSize: 17 }} />}
                                    </IconButton>
                                  </span>
                                </Tooltip>

                                <Menu
                                  anchorEl={moreMenuAnchor[testId]}
                                  open={Boolean(moreMenuAnchor[testId])}
                                  onClose={() => setMoreMenuAnchor((prev) => ({ ...prev, [testId]: null }))}
                                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                  PaperProps={{ sx: { minWidth: 220, borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' } }}
                                >
                                  {canAccessAssignmentTests && [
                                    <MenuItem
                                      key="q-paper"
                                      onClick={() => { handleDownloadPdf(test, 'question-paper'); setMoreMenuAnchor((prev) => ({ ...prev, [testId]: null })); }}
                                      disabled={isDownloadingCurrentTest}
                                    >
                                      <ListItemIcon><DownloadIcon fontSize="small" color="info" /></ListItemIcon>
                                      Question paper
                                    </MenuItem>,
                                    <MenuItem
                                      key="a-sheet"
                                      onClick={() => { handleDownloadPdf(test, 'answer-sheet'); setMoreMenuAnchor((prev) => ({ ...prev, [testId]: null })); }}
                                      disabled={isDownloadingCurrentTest}
                                    >
                                      <ListItemIcon><DownloadIcon fontSize="small" color="info" /></ListItemIcon>
                                      Answer sheet
                                    </MenuItem>,
                                    <MenuItem
                                      key="a-key"
                                      onClick={() => { handleDownloadPdf(test, 'answers'); setMoreMenuAnchor((prev) => ({ ...prev, [testId]: null })); }}
                                      disabled={isDownloadingCurrentTest}
                                    >
                                      <ListItemIcon><DownloadIcon fontSize="small" color="info" /></ListItemIcon>
                                      Answer key
                                    </MenuItem>,
                                  ]}

                                  {canUpdateAssignments && [
                                    <Divider key="divider-1" />,
                                    <MenuItem
                                      key="rename"
                                      onClick={() => { setMoreMenuAnchor((prev) => ({ ...prev, [testId]: null })); startRename(test); }}
                                    >
                                      <ListItemIcon><DriveFileRenameOutlineIcon fontSize="small" color="primary" /></ListItemIcon>
                                      Rename
                                    </MenuItem>,
                                    <MenuItem
                                      key="toggle"
                                      onClick={() => { setMoreMenuAnchor((prev) => ({ ...prev, [testId]: null })); handleToggleStatusClick(test); }}
                                      disabled={togglingStatusTestId === testId}
                                    >
                                      <ListItemIcon>
                                        {isTestActive(test)
                                          ? <ToggleOffIcon fontSize="small" color="warning" />
                                          : <ToggleOnIcon fontSize="small" color="success" />}
                                      </ListItemIcon>
                                      {isTestActive(test) ? 'Deactivate' : 'Activate'}
                                    </MenuItem>,
                                    <Divider key="divider-2" />,
                                    <MenuItem
                                      key="gen-videos"
                                      onClick={() => { setMoreMenuAnchor((prev) => ({ ...prev, [testId]: null })); handleVideoGenerate(test); }}
                                      disabled={generatingVideosTestId === testId}
                                    >
                                      <ListItemIcon><MovieCreationIcon fontSize="small" color="success" /></ListItemIcon>
                                      Generate explanation videos
                                    </MenuItem>,
                                    <MenuItem
                                      key="rm-videos"
                                      onClick={() => { setMoreMenuAnchor((prev) => ({ ...prev, [testId]: null })); handleRemoveAllVideos(test); }}
                                      disabled={removingVideosTestId === testId}
                                    >
                                      <ListItemIcon><DeleteSweepIcon fontSize="small" color="error" /></ListItemIcon>
                                      Remove all videos
                                    </MenuItem>,
                                  ]}
                                </Menu>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </DraggableTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => {
                setPage(newPage);
                fetchTests(true, { page: newPage });
              }}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                const rpp = parseInt(e.target.value, 10);
                setRowsPerPage(rpp);
                setPage(0);
                fetchTests(true, { page: 0, rowsPerPage: rpp });
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </CardContent>
        </Card>
      )}

      {/* Test Details Modal */}
      <Dialog 
        open={modalOpen} 
        onClose={() => setModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ 
              background: 'rgba(255,255,255,0.2)',
              width: 48,
              height: 48
            }}>
              <QuizIcon sx={{ fontSize: 24, color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {selectedTest?.test_name}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Test Details & Questions
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={() => setModalOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ 
          p: 4, 
          flex: '1 1 auto', 
          overflow: 'hidden', 
          display: 'flex', 
          flexDirection: 'column',
          minHeight: 0
        }}>
          {selectedTest && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              flex: '1 1 auto',
              minHeight: 0,
              overflow: 'hidden' 
            }}>
              {/* Test Information */}
              <Card sx={{ 
                mb: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                border: '1px solid rgba(102, 126, 234, 0.2)',
                flexShrink: 0
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    📋 Test Information
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 3,
                    flexWrap: 'wrap'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Course:
                      </Typography>
                      {Array.isArray(selectedTest.assignments) && selectedTest.assignments.length > 0 ? (
                        selectedTest.assignments.map((assignment) => (
                          <Box key={assignment.id} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            <Chip
                              icon={<SchoolIcon />}
                              label={`${displayCourseName(assignment.academic_course_label || 'Course')} • ${assignment.week_label || 'Week'}`}
                              size="small"
                              sx={{ background: '#f3e5f5', color: '#6a1b9a', fontWeight: 600 }}
                            />
                            {assignment.plan_name && (
                              <Chip
                                size="small"
                                label={`${assignment.plan_name} · Week ${assignment.plan_week_number ?? '—'}`}
                                sx={{ background: '#e0e7ff', color: '#4338ca', fontWeight: 600 }}
                              />
                            )}
                          </Box>
                        ))
                      ) : (
                        <Chip
                          icon={<SchoolIcon />}
                          label={displayCourseName(selectedTest.course_name)}
                          size="small"
                          sx={{
                            background: '#e3f2fd',
                            color: '#1976d2',
                            fontWeight: 600
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Subject:
                      </Typography>
                      <Chip 
                        label={selectedTest.subject_name} 
                        size="small"
                        sx={{ 
                          background: '#e8f5e8', 
                          color: '#2e7d32',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Topic:
                      </Typography>
                      <Chip 
                        icon={<TopicIcon />}
                        label={selectedTest.topic_name} 
                        size="small"
                        sx={{ 
                          background: '#fff3e0', 
                          color: '#f57c00',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                    {selectedTest.subtopic_name && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Subtopic:
                        </Typography>
                        <Chip 
                          label={selectedTest.subtopic_name} 
                          size="small"
                          sx={{ 
                            background: '#fce4ec', 
                            color: '#c2185b',
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Total Questions:
                      </Typography>
                      <Badge 
                        badgeContent={selectedTest.questions?.length || 0} 
                        color="primary"
                        sx={{
                          '& .MuiBadge-badge': {
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            fontSize: '14px',
                            fontWeight: 700,
                            px: 1
                          }
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          Questions
                        </Typography>
                      </Badge>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Questions Accordion View */}
              <Card sx={{ 
                borderRadius: 3,
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                flex: '1 1 auto',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0
              }}>
                <CardContent sx={{ 
                  p: 0, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflow: 'hidden'
                }}>
                  <Box sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    p: 3,
                    flexShrink: 0
                  }}>
                    <Typography variant="h6" fontWeight={600}>
                      📝 Test Questions ({selectedTest.questions?.length || 0})
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    p: 2, 
                    flex: '1 1 auto', 
                    overflowY: 'auto', 
                    overflowX: 'hidden',
                    minHeight: 0,
                    maxHeight: '100%'
                  }}>
                    {selectedTest.questions?.map((q, qIdx) => {
                      const visualAsset = resolveVisualAsset(q.visual_image_url, q.visual);
                      const questionText = getQuestionText(q);
                      const questionExplanation = getQuestionExplanation(q);
                      const questionImageUrls = collectImageUrls(q.image_url, q.image_urls, q.question_image_url, q.question_image_urls);
                      const isCorrectAnswer = (optName) => q.correct_answer && q.correct_answer === optName;
                      
                      return (
                        <Accordion 
                          key={qIdx} 
                          defaultExpanded={qIdx === 0}
                          sx={{ 
                            mb: 2,
                            borderRadius: 2,
                            '&:before': { display: 'none' },
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1, flexWrap: 'wrap', width: '100%' }}>
                                <Typography component="span" sx={{ fontWeight: 600, flexShrink: 0 }}>
                                  {qIdx + 1}.
                                </Typography>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <PreviewLatex html={questionText} />
                                </Box>
                                {q.is_flagged && (
                                  <Chip 
                                    label="Flagged"
                                    color="warning"
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                {q.subject && <Chip size="small" label={q.subject} />}
                                {q.exam_board && <Chip size="small" label={`Board: ${q.exam_board}`} />}
                                {q.difficulty && <Chip size="small" label={`Difficulty: ${q.difficulty}`} />}
                                {q.strand && <Chip size="small" label={`Strand: ${q.strand}`} />}
                                {q.subSubject && <Chip size="small" label={`Topic: ${q.subSubject}`} />}
                                {q.question_type && <Chip size="small" label={`Type: ${q.question_type}`} />}
                                {q.duration_in_sec && (
                                  <Chip 
                                    icon={<TimerIcon />}
                                    size="small" 
                                    label={`${q.duration_in_sec}s`} 
                                  />
                                )}
                                {IN_PROGRESS_VIDEO_STATUSES.includes(q.video_generation_status) && (
                                  <Chip
                                    size="small"
                                    label={`Video: ${String(q.video_generation_status).replace(/_/g, ' ')}…`}
                                    color="info"
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                  />
                                )}
                                {q.video_generation_status === 'failed' && (
                                  <Chip size="small" label="Video generation failed" color="error" variant="outlined" sx={{ fontWeight: 600 }} />
                                )}
                                {q.explanation_video_url && !IN_PROGRESS_VIDEO_STATUSES.includes(q.video_generation_status) && (
                                  <Chip
                                    icon={<VideoFileIcon />}
                                    size="small"
                                    label="Explanation video"
                                    color="success"
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                  />
                                )}
                              </Box>
                              {visualAsset && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Visual reference
                                  </Typography>
                                  <Box
                                    sx={{
                                      p: 1,
                                      borderRadius: 2,
                                      border: '1px solid rgba(0,0,0,0.08)',
                                      background: '#fff',
                                      maxWidth: 420,
                                      overflow: 'hidden',
                                      position: 'relative',
                                    }}
                                  >
                                    {visualAsset.type === 'image' ? (
                                      <>
                                        {imageLoading.has(`q-${qIdx}`) && !imageErrors.has(`q-${qIdx}`) && (
                                          <Box
                                            sx={{
                                              width: '100%',
                                              minHeight: 200,
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              background: '#f5f5f5',
                                            }}
                                          >
                                            <CircularProgress size={30} />
                                          </Box>
                                        )}
                                        {!imageLoading.has(`q-${qIdx}`) && !imageErrors.has(`q-${qIdx}`) && (
                                          <img
                                            alt={visualAsset.alt || 'Question visual'}
                                            src={visualAsset.src}
                                            onLoad={() => {
                                              setImageLoading((prev) => {
                                                const next = new Set(prev);
                                                next.delete(`q-${qIdx}`);
                                                return next;
                                              });
                                            }}
                                            onError={() => {
                                              setImageLoading((prev) => {
                                                const next = new Set(prev);
                                                next.delete(`q-${qIdx}`);
                                                return next;
                                              });
                                              setImageErrors((prev) => new Set([...prev, `q-${qIdx}`]));
                                            }}
                                            onLoadStart={() => {
                                              setImageLoading((prev) => new Set([...prev, `q-${qIdx}`]));
                                            }}
                                            style={{
                                              width: '100%',
                                              height: 'auto',
                                              display: imageLoading.has(`q-${qIdx}`) ? 'none' : 'block',
                                            }}
                                          />
                                        )}
                                        {imageErrors.has(`q-${qIdx}`) && (
                                          <Box
                                            sx={{
                                              width: '100%',
                                              minHeight: 200,
                                              display: 'flex',
                                              flexDirection: 'column',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              background: '#f5f5f5',
                                              p: 2,
                                            }}
                                          >
                                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                                              {visualAsset.alt || 'Question visual'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                              Image could not be loaded
                                            </Typography>
                                          </Box>
                                        )}
                                      </>
                                    ) : (
                                      <Box
                                        component="div"
                                        role="img"
                                        aria-label={visualAsset.alt || 'Question visual'}
                                        sx={{
                                          width: '100%',
                                          '& svg': {
                                            width: '100%',
                                            height: 'auto',
                                            display: 'block',
                                          },
                                        }}
                                        dangerouslySetInnerHTML={{ __html: visualAsset.svg }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              )}
                              {questionImageUrls.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Question images
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {questionImageUrls.map((src, idx) => (
                                      <Box
                                        key={`${src}-${idx}`}
                                        component="img"
                                        src={src}
                                        alt={`Question ${qIdx + 1} image ${idx + 1}`}
                                        sx={{
                                          width: 140,
                                          maxWidth: '100%',
                                          borderRadius: 1.5,
                                          border: '1px solid rgba(0,0,0,0.12)',
                                          objectFit: 'contain',
                                          background: '#fff',
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}
                              {q.passage_text && (
                                <Box sx={{ p: 2, bgcolor: getAdminQuestionCategory(q.question_type) !== 'reading_comprehension' ? '#faf8f5' : 'grey.50', borderRadius: 1, borderLeft: getAdminQuestionCategory(q.question_type) !== 'reading_comprehension' ? '4px solid #8d6e63' : 'none', mb: 1, mt: 2 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {getAdminQuestionCategory(q.question_type) !== 'reading_comprehension' ? 'SHARED PASSAGE' : 'PASSAGE'}
                                  </Typography>
                                  {passageEditState?.questionId === q.id ? (
                                    <Box sx={{ mt: 1 }}>
                                      <TextField
                                        multiline
                                        minRows={4}
                                        fullWidth
                                        value={passageEditState.text}
                                        onChange={(e) => setPassageEditState((prev) => ({ ...prev, text: e.target.value }))}
                                      />
                                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                        <Button size="small" variant="contained" disabled={passageEditState.saving} onClick={savePassageEdit}>
                                          Save
                                        </Button>
                                        <Button size="small" variant="outlined" onClick={() => setPassageEditState(null)}>
                                          Cancel
                                        </Button>
                                      </Box>
                                    </Box>
                                  ) : (
                                    <>
                                      <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{q.passage_text}</Typography>
                                      {canUpdateAssignments && (
                                        <Button size="small" sx={{ mt: 1 }} onClick={() => openPassageEdit(q)}>
                                          Edit passage
                                        </Button>
                                      )}
                                    </>
                                  )}
                                </Box>
                              )}
                              {q.audio_url && (
                                <audio controls src={q.audio_url} style={{ width: '100%', marginTop: 8 }} />
                              )}
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid container spacing={2}>
                              {canUpdateAssignments && (
                                <Grid item xs={12}>
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                                      onClick={() => handleEditQuestion(qIdx)}
                                      sx={{ textTransform: 'none' }}
                                    >
                                      Edit question
                                    </Button>
                                  </Box>
                                </Grid>
                              )}
                              <Grid item xs={12}>
                                {/* ── Type-aware question content ── */}
                                {(() => {
                                  const cat = getAdminQuestionCategory(q.question_type);

                                  if (cat === 'mcq') {
                                    return (
                                      <>
                                        <Typography sx={{ fontWeight: 700, mb: 1 }}>Options</Typography>
                                        <Box sx={{ display: 'grid', gap: 1 }}>
                                          {q.options?.map((opt, i) => {
                                            const optionText = getOptionText(opt);
                                            const optionImageUrls = collectImageUrls(opt.image_url, opt.image_urls, opt.option_image_url, opt.option_image_urls);
                                            const isCorrect = isCorrectAnswer(optionText);
                                            return (
                                              <Box key={i} sx={{ p: 1.2, borderRadius: 1, border: '1px solid', background: isCorrect ? 'rgba(76,175,80,0.12)' : '#fff', borderColor: isCorrect ? 'rgba(76,175,80,0.3)' : 'rgba(0,0,0,0.08)' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                                  <Typography component="span" variant="body2" sx={{ fontWeight: 700, flexShrink: 0 }}>
                                                    {String.fromCharCode(65 + i)}.
                                                  </Typography>
                                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <PreviewLatex html={optionText} />
                                                  </Box>
                                                </Box>
                                                {optionImageUrls.length > 0 && (
                                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                                    {optionImageUrls.map((src, imgIdx) => (
                                                      <Box
                                                        key={`${src}-${imgIdx}`}
                                                        component="img"
                                                        src={src}
                                                        alt={`Option ${String.fromCharCode(65 + i)} image ${imgIdx + 1}`}
                                                        sx={{ width: 110, maxWidth: '100%', borderRadius: 1, border: '1px solid rgba(0,0,0,0.12)', objectFit: 'contain', background: '#fff' }}
                                                      />
                                                    ))}
                                                  </Box>
                                                )}
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                        {q.correct_answer && (
                                          <Alert severity="success" sx={{ mt: 2 }}>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 0.5 }}>
                                              <span>Correct answer:</span>
                                              <PreviewLatex html={q.correct_answer} inline />
                                            </Box>
                                          </Alert>
                                        )}
                                      </>
                                    );
                                  }

                                  if (cat === 'fill_blank') {
                                    return (
                                      <>
                                        <Typography sx={{ fontWeight: 700, mb: 1 }}>Fill in the Blank — Correct Answer</Typography>
                                        <Box sx={{ display: 'grid', gap: 1 }}>
                                          {q.options?.map((opt, i) => (
                                            <Box key={i} sx={{ p: 1.2, borderRadius: 1, border: '1px solid rgba(76,175,80,0.3)', background: 'rgba(76,175,80,0.1)' }}>
                                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                                <Typography component="span" variant="body2">✓</Typography>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                  <PreviewLatex html={getOptionText(opt)} />
                                                </Box>
                                              </Box>
                                            </Box>
                                          ))}
                                        </Box>
                                        {q.correct_answer && (
                                          <Alert severity="success" sx={{ mt: 2 }}>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 0.5 }}>
                                              <span>Answer:</span>
                                              <PreviewLatex html={q.correct_answer} inline />
                                            </Box>
                                          </Alert>
                                        )}
                                      </>
                                    );
                                  }

                                  if (cat === 'short_answer') {
                                    return (
                                      <>
                                        <Typography sx={{ fontWeight: 700, mb: 1 }}>Short Answer — Model Answer</Typography>
                                        {q.options?.map((opt, i) => (
                                          <Box key={i} sx={{ p: 1.5, mb: 0.75, borderRadius: 1, border: '1px solid rgba(76,175,80,0.3)', background: 'rgba(76,175,80,0.08)', lineHeight: 1.7 }}>
                                            <PreviewLatex html={getOptionText(opt)} />
                                          </Box>
                                        ))}
                                        {q.correct_answer && !q.options?.length && (
                                          <Alert severity="success" sx={{ mt: 1 }}>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 0.5 }}>
                                              <span>Model answer:</span>
                                              <PreviewLatex html={q.correct_answer} inline />
                                            </Box>
                                          </Alert>
                                        )}
                                      </>
                                    );
                                  }

                                  if (cat === 'vocab_matching') {
                                    return (
                                      <>
                                        <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Vocabulary Matching — Word Pairs</Typography>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                          <Typography variant="caption" sx={{ fontWeight: 700, p: 0.75, bgcolor: '#f3f4f6', borderRadius: 1, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Words</Typography>
                                          <Typography variant="caption" sx={{ fontWeight: 700, p: 0.75, bgcolor: '#f3f4f6', borderRadius: 1, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Definition / Match</Typography>
                                          {q.options?.map((opt, i) => (
                                            <React.Fragment key={i}>
                                              <Box sx={{ p: 1.2, borderRadius: 1, border: '1px solid rgba(0,0,0,0.08)', bgcolor: '#fff' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                                  <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>{i + 1}.</Typography>
                                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <PreviewLatex html={getOptionText(opt)} />
                                                  </Box>
                                                </Box>
                                              </Box>
                                              <Box sx={{ p: 1.2, borderRadius: 1, border: '1px solid rgba(76,175,80,0.3)', bgcolor: 'rgba(76,175,80,0.08)' }}>
                                                <PreviewLatex html={opt.match_text || opt.definition || getOptionText(opt)} />
                                              </Box>
                                            </React.Fragment>
                                          ))}
                                        </Box>
                                      </>
                                    );
                                  }

                                  if (cat === 'writing') {
                                    return (
                                      <>
                                        <Alert severity="info" sx={{ mb: 1.5 }}>Writing Composition — open-ended task, no single correct answer</Alert>
                                        {q.options?.length > 0 && (
                                          <>
                                            <Typography sx={{ fontWeight: 700, mb: 1 }}>Writing Prompt / Instructions</Typography>
                                            {q.options.map((opt, i) => (
                                              <Box key={i} sx={{ p: 1.5, mb: 0.75, borderRadius: 1, bgcolor: '#f8f9fa', border: '1px solid #e5e7eb' }}>
                                                <PreviewLatex html={getOptionText(opt)} />
                                              </Box>
                                            ))}
                                          </>
                                        )}
                                      </>
                                    );
                                  }

                                  if (cat === 'reading_comprehension') {
                                    const subQs = q.ai_metadata?.sub_questions || q.sub_questions || [];
                                    return (
                                      <>
                                        <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Reading Comprehension — Sub-questions</Typography>
                                        {subQs.map((sq, sqIdx) => (
                                          <Box key={sqIdx} sx={{ mb: 2, p: 1.5, borderRadius: 1, border: '1px solid rgba(0,0,0,0.08)', bgcolor: '#fff' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                                              {sqIdx + 1}. {sq.question}
                                            </Typography>
                                            <Box sx={{ display: 'grid', gap: 0.75 }}>
                                              {(sq.options || []).map((opt, optIdx) => {
                                                const isCorrect = sq.answer && String(sq.answer).trim() === String(opt).trim();
                                                return (
                                                  <Box
                                                    key={optIdx}
                                                    sx={{
                                                      p: 1,
                                                      borderRadius: 1,
                                                      border: '1px solid',
                                                      borderColor: isCorrect ? 'rgba(76,175,80,0.3)' : 'rgba(0,0,0,0.08)',
                                                      bgcolor: isCorrect ? 'rgba(76,175,80,0.12)' : '#fff',
                                                    }}
                                                  >
                                                    <Typography variant="body2">
                                                      {String.fromCharCode(65 + optIdx)}. {opt}
                                                    </Typography>
                                                  </Box>
                                                );
                                              })}
                                            </Box>
                                          </Box>
                                        ))}
                                      </>
                                    );
                                  }

                                  return null;
                                })()}

                                {hasRenderableRichText(questionExplanation) && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Explanation</Typography>
                                    <Box
                                      sx={{
                                        p: 1.25,
                                        borderRadius: 1,
                                        border: '1px solid rgba(25,118,210,0.2)',
                                        background: 'rgba(227,242,253,0.45)',
                                        '& img': { maxWidth: '100%', height: 'auto', display: 'block', borderRadius: 1, my: 1 },
                                      }}
                                    >
                                      <PreviewLatex html={questionExplanation} />
                                    </Box>
                                  </Box>
                                )}

                                {canUpdateAssignments && (
                                <Grid item xs={12} sx={{ mt: 2 }}>
                                  <Typography sx={{ fontWeight: 700, mb: 1 }}>Explanation video</Typography>
                                  {q.explanation_video_url ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                                      <VideoExplanationPlayer
                                        videoUrl={q.explanation_video_url}
                                        questionText={questionText}
                                      />
                                      <Button
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        disabled={actionQuestionId === q.id}
                                        onClick={async () => {
                                          setActionQuestionId(q.id);
                                          try {
                                            await api.post(`admin/assign/test/${selectedTestId}/questions/${q.id}/regenerate-video`);
                                            toast.success('Video regeneration queued');
                                            await refetchSelectedTest();
                                          } catch (err) {
                                            toast.error(err?.response?.data?.message || 'Failed to queue regeneration');
                                          } finally {
                                            setActionQuestionId(null);
                                          }
                                        }}
                                      >
                                        Regenerate video
                                      </Button>
                                      <Button
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                        disabled={actionQuestionId === q.id}
                                        onClick={async () => {
                                          setActionQuestionId(q.id);
                                          try {
                                            await api.delete(`admin/assign/test/${selectedTestId}/questions/${q.id}/explanation-video`);
                                            toast.success('Video removed');
                                            await refetchSelectedTest();
                                          } catch (err) {
                                            toast.error(err.response?.data?.message || 'Failed to remove video');
                                          } finally {
                                            setActionQuestionId(null);
                                          }
                                        }}
                                      >
                                        Remove video
                                      </Button>
                                    </Box>
                                  ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2" color="text.secondary">No video</Typography>
                                      <Button
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        disabled={actionQuestionId === q.id}
                                        onClick={async () => {
                                          setActionQuestionId(q.id);
                                          try {
                                            await api.post(`admin/assign/test/${selectedTestId}/questions/${q.id}/regenerate-video`);
                                            toast.success('Video regeneration queued');
                                            await refetchSelectedTest();
                                          } catch (err) {
                                            toast.error(err?.response?.data?.message || 'Failed to queue regeneration');
                                          } finally {
                                            setActionQuestionId(null);
                                          }
                                        }}
                                      >
                                        Regenerate video
                                      </Button>
                                    </Box>
                                  )}
                                </Grid>
                                )}
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
          <Button 
            onClick={() => setModalOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Close
          </Button>
          {selectedTest && canAccessAssignmentTests && (
            <>
              <Tooltip title="Download">
                <span>
                  <IconButton
                    color="info"
                    disabled={isModalDownloadingAny}
                    onClick={(e) => setModalDownloadMenuAnchor(e.currentTarget)}
                    sx={{
                      border: '1px solid',
                      borderColor: 'info.main',
                      borderRadius: 2,
                    }}
                  >
                    {isModalDownloadingAny ? (
                      <CircularProgress size={22} />
                    ) : (
                      <DownloadIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Menu
                anchorEl={modalDownloadMenuAnchor}
                open={Boolean(modalDownloadMenuAnchor)}
                onClose={() => setModalDownloadMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <MenuItem onClick={() => { handleDownloadPdf(selectedTest, 'paper'); setModalDownloadMenuAnchor(null); }}>
                  Question paper
                </MenuItem>
                <MenuItem onClick={() => { handleDownloadPdf(selectedTest, 'answers'); setModalDownloadMenuAnchor(null); }}>
                  Answer key
                </MenuItem>
              </Menu>
            </>
          )}
          {selectedTest && canUpdateAssignments && (
            <>
              <Tooltip title="Explanation videos">
                <span>
                  <IconButton
                    color="success"
                    disabled={
                      generatingVideosTestId === selectedTestId ||
                      removingVideosTestId === selectedTestId
                    }
                    onClick={(e) => setModalVideoMenuAnchor(e.currentTarget)}
                    sx={{
                      border: '1px solid',
                      borderColor: 'success.main',
                      borderRadius: 2,
                    }}
                  >
                    {generatingVideosTestId === selectedTestId ||
                    removingVideosTestId === selectedTestId ? (
                      <CircularProgress size={22} />
                    ) : (
                      <SmartDisplayIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Menu
                anchorEl={modalVideoMenuAnchor}
                open={Boolean(modalVideoMenuAnchor)}
                onClose={() => setModalVideoMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <MenuItem
                  onClick={() => {
                    setModalVideoMenuAnchor(null);
                    handleVideoGenerate(selectedTest);
                  }}
                  disabled={generatingVideosTestId === selectedTestId}
                >
                  <ListItemIcon>
                    <MovieCreationIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  Generate explanation videos
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setModalVideoMenuAnchor(null);
                    handleRemoveAllVideos(selectedTest);
                  }}
                  disabled={removingVideosTestId === selectedTestId}
                >
                  <ListItemIcon>
                    <DeleteSweepIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  Remove all videos
                </MenuItem>
              </Menu>
            </>
          )}
          {selectedTest && canUpdateAssignments && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => {
                const testId = selectedTest.id || selectedTest.test_id || selectedTest.ID;
                if (testId) {
                  setModalOpen(false);
                  navigate(`/admin/course-test/${testId}`);
                } else {
                  toast.error('Cannot edit: Test ID not found');
                }
              }}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 3,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Edit Test
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
          color: 'error.main',
          pb: 1
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: 'error.light',
            color: 'error.main',
            fontSize: '20px'
          }}>
            ⚠️
          </Box>
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            Are you sure you want to delete this test? This action cannot be undone.
          </Typography>
          {deleteDialog.test && (
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'grey.50', 
              borderRadius: 1, 
              border: '1px solid', 
              borderColor: 'grey.300' 
            }}>
              <Typography variant="subtitle1" fontWeight={600}>
                <strong>Test:</strong> {deleteDialog.test.test_name || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Topic:</strong> {deleteDialog.test.topic_name || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Questions:</strong> {deleteDialog.test.questions?.length || 0}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1.5 }}>
          <Button 
            onClick={handleDeleteCancel}
            variant="outlined"
            color="inherit"
            size="large"
            sx={{ 
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            size="large"
            disabled={deletingTestId === (deleteDialog.test?.id || deleteDialog.test?.test_id || deleteDialog.test?.ID)}
            autoFocus
            sx={{ 
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            {deletingTestId === (deleteDialog.test?.id || deleteDialog.test?.test_id || deleteDialog.test?.ID) ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Deleting...
              </Box>
            ) : (
              'Delete Test'
            )}
          </Button>
        </DialogActions>
        </Dialog>

      {/* Deactivate / Activate Confirmation Dialog */}
      <Dialog
        open={statusDialog.open}
        onClose={handleToggleStatusCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          color: statusDialog.action === 'deactivate' ? 'warning.dark' : 'success.dark',
          pb: 1
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: statusDialog.action === 'deactivate' ? 'warning.light' : 'success.light',
            fontSize: '20px'
          }}>
            {statusDialog.action === 'deactivate' ? '⛔' : '✅'}
          </Box>
          {statusDialog.action === 'deactivate' ? 'Confirm Deactivate' : 'Confirm Activate'}
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            {statusDialog.action === 'deactivate'
              ? 'Are you sure you want to deactivate this test? It will no longer be visible to students.'
              : 'Are you sure you want to activate this test? It will become visible to students.'}
          </Typography>
          {statusDialog.test && (
            <Box sx={{
              p: 2,
              backgroundColor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300'
            }}>
              <Typography variant="subtitle1" fontWeight={600}>
                <strong>Test:</strong> {statusDialog.test.test_name || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Topic:</strong> {statusDialog.test.topic_name || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Current status:</strong> {isTestActive(statusDialog.test) ? 'Active' : 'Inactive'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1.5 }}>
          <Button
            onClick={handleToggleStatusCancel}
            variant="outlined"
            color="inherit"
            size="large"
            sx={{
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleToggleStatusConfirm}
            variant="contained"
            color={statusDialog.action === 'deactivate' ? 'warning' : 'success'}
            size="large"
            disabled={togglingStatusTestId === (statusDialog.test?.id || statusDialog.test?.test_id || statusDialog.test?.ID)}
            autoFocus
            sx={{
              minWidth: 120,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            {togglingStatusTestId === (statusDialog.test?.id || statusDialog.test?.test_id || statusDialog.test?.ID) ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                {statusDialog.action === 'deactivate' ? 'Deactivating...' : 'Activating...'}
              </Box>
            ) : (
              statusDialog.action === 'deactivate' ? 'Deactivate Test' : 'Activate Test'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignmentViewDialog.open}
        onClose={() => setAssignmentViewDialog({ open: false, test: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon color="primary" />
          Course / Week Assignments
        </DialogTitle>
        <DialogContent dividers>
          {assignmentViewDialog.test?.test_name && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {assignmentViewDialog.test.test_name}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(assignmentViewDialog.test?.assignments || []).map((assignment) => (
              <Chip
                key={assignment.id}
                icon={<SchoolIcon />}
                label={`${displayCourseName(assignment.academic_course_label || 'Course')} • ${assignment.week_label || 'Week'}`}
                sx={{
                  ...LIST_CHIP_MULTILINE_SX,
                  background: '#f3e5f5',
                  color: '#6a1b9a',
                  fontWeight: 500,
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignmentViewDialog({ open: false, test: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </TestDndContext>
  );
};

export default TestList;
